import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { DropDownMenuOption, MultiSelectDropDownOption } from '@replai-platform/ui-components';
import { RootState } from '../../../store/rootReducer';
import { ProjectActions } from '../../../store/project';
import useGetCampaigns from '../../../api/hooks/campaigns/useGetCampaigns';
import { FilterActions } from '../../../store/filters';
import { Page } from '../../../utils/enums';

const useCampaignFilter = ({
  initialOptions,
  excludedOptions,
  page,
}: {
  initialOptions?: string[];
  /** When set, every option except these is selected (takes precedence over `initialOptions`) */
  excludedOptions?: string[];
  page?: Page;
}): {
  campaignFilterOptions: DropDownMenuOption[];
  isLoading: boolean;
  hasData: boolean;
  campaignFilterOnChange: (selectedOptions: MultiSelectDropDownOption[]) => void;
} => {
  const dispatch = useDispatch();
  const projectId = useSelector((state: RootState) => state.project.id);
  const { endDate, startDate } = useSelector((state: RootState) => state.filters);

  const isPageDashboard = page === Page.Dashboard;

  // Fetch and update project networks
  const { isSuccess, data, isLoading } = useGetCampaigns({
    projectIds: [projectId],
    metricsFilters: isPageDashboard
      ? {
          countriesToConsider: ['ALL'],
          dateEndTimestamp: new Date(endDate).getTime(),
          dateStartTimestamp: new Date(startDate).getTime(),
        }
      : undefined,
  });

  const mappedCampaigns = useMemo(
    () => data?.campaigns?.map(({ id, name }) => ({ campaignId: id, campaignName: name })) ?? [],
    [data]
  );

  useEffect(() => {
    if (isSuccess && data && data?.campaigns.length > 0) {
      dispatch(ProjectActions.changeCampaigns(data));
    }
  }, [mappedCampaigns, data, isSuccess, dispatch]);

  // Create filter options
  const campaignFilterOptions = useMemo(() => {
    if (!isSuccess || !data) return [];
    return [
      {
        label: 'All Campaigns',
        isAllOption: true,
        selected: !initialOptions,
      },
      ...mappedCampaigns.map((campaign) => ({
        label: campaign.campaignName,
        id: campaign.campaignId,
        selected: excludedOptions?.length
          ? !excludedOptions.includes(campaign.campaignId)
          : !initialOptions
          ? !isPageDashboard
          : initialOptions.includes(campaign.campaignId),
      })),
    ];
  }, [mappedCampaigns, data, isSuccess, initialOptions, excludedOptions, isPageDashboard]);

  const campaignFilterOnChange = (selectedOptions: MultiSelectDropDownOption[]) => {
    const selectedCampaigns = selectedOptions
      .filter((option) => option.selected && option.id)
      .flatMap((option) => option.id?.split(';') ?? []);

    dispatch(
      FilterActions.changeCampaigns({
        value: selectedCampaigns,
        toExclude: false,
        logEvent: true,
        logComponent: 'CampaignFilter Dashboard',
      })
    );
  };

  return { campaignFilterOptions, isLoading, hasData: campaignFilterOptions.length > 0, campaignFilterOnChange };
};

export default useCampaignFilter;
